import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import {
  useEffectivePermissions,
  useUserEffectivePermissions,
  useCustomPermissions,
  addCustomPermission,
  removeCustomPermission,
  CustomPermission,
  PermissionType,
} from "../../hooks/usePermission";
import {
  PermissionLoadingState,
  PermissionRequired,
  EmptyPermissionState,
} from "../permissions";

interface UserPermissionsViewProps {
  associationId: number | undefined;
  userId?: number;
  refreshKey?: number;
  /** If provided, display these permissions directly instead of fetching */
  permissions?: Record<string, string[]>;
  /** If true, shows loading state */
  isLoading?: boolean;
  /** If true, SuperAdmin can click to toggle permissions */
  editable?: boolean;
  /** Callback when permissions change (for refreshing parent) */
  onPermissionChange?: () => void;
}

// Finnish translations for resources
const resourceLabels: Record<string, string> = {
  invoice: "Laskutus",
  meeting: "Kokoukset",
  user: "Käyttäjät",
  association: "Tiekunta",
  unit: "Yksiköinti",
  property: "Kiinteistöt",
  file: "Tiedostot",
  voucher: "Tositteet",
  account: "Tilit",
  road: "Tiet",
  membership: "Jäsenyydet",
};

// Finnish translations for actions
const actionLabels: Record<string, string> = {
  create: "Luonti",
  read: "Luku",
  update: "Muokkaus",
  delete: "Poisto",
};

const allActions = ["create", "read", "update", "delete"];

// All resources that can have permissions
const allResources = [
  "invoice",
  "meeting",
  "user",
  "association",
  "property",
  "road",
  "file",
  "voucher",
  "account",
];

/**
 * Component to display user's effective permissions in an association
 * Shows a table of resources and what actions the user can perform
 * SuperAdmin can click cells to toggle GRANT/DENY custom permissions
 */
export const UserPermissionsView: React.FC<UserPermissionsViewProps> = ({
  associationId,
  userId,
  refreshKey,
  permissions: providedPermissions,
  isLoading: providedLoading,
  editable = false,
  onPermissionChange,
}) => {
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Use different hooks based on whether we're viewing another user or current user
  const currentUserPermissions = useEffectivePermissions(associationId);
  const specificUserPermissions = useUserEffectivePermissions(
    associationId,
    userId,
    refreshKey
  );

  // Fetch custom permissions if editable
  const {
    customPermissions,
    loading: customLoading,
    refetch: refetchCustom,
  } = useCustomPermissions(
    editable ? associationId : undefined,
    editable ? userId : undefined,
    refreshKey
  );

  // If permissions are provided directly, use them
  // Otherwise, select based on whether userId is provided
  const fetchedData = userId ? specificUserPermissions : currentUserPermissions;

  const permissions = providedPermissions ?? fetchedData.permissions;
  const loading = providedLoading ?? fetchedData.loading;
  const error = providedPermissions ? null : fetchedData.error;

  if (loading || (editable && customLoading)) {
    return <PermissionLoadingState variant="skeleton" />;
  }

  if (error) {
    return (
      <PermissionRequired
        resource="user"
        action="read"
        message={`Virhe ladattaessa käyttöoikeuksia: ${error}`}
        variant="alert"
        showContactAdmin={false}
      />
    );
  }

  if (!associationId) {
    return (
      <EmptyPermissionState
        variant="no-data"
        title="Tiekunta puuttuu"
        message="Valitse tiekunta nähdäksesi käyttöoikeudet."
      />
    );
  }

  // Get resources from permissions or use all resources in editable mode
  const resources = editable ? allResources : Object.keys(permissions).sort();

  if (resources.length === 0 && !editable) {
    return (
      <EmptyPermissionState
        variant="no-permission"
        title="Ei käyttöoikeuksia"
        message="Sinulla ei ole käyttöoikeuksia tässä tiekunnassa. Ota yhteyttä järjestelmänvalvojaan."
      />
    );
  }

  // Helper to find custom permission for resource+action
  const findCustomPermission = (
    resource: string,
    action: string
  ): CustomPermission | undefined => {
    return customPermissions.find(
      (cp) => cp.resource === resource && cp.actions.includes(action)
    );
  };

  // Helper to get permission status
  const getPermissionStatus = (
    resource: string,
    action: string
  ): { hasPermission: boolean; customType?: PermissionType } => {
    const actions = permissions[resource] || [];
    const hasPermission = actions.includes(action);
    const custom = findCustomPermission(resource, action);

    return {
      hasPermission,
      customType: custom?.type,
    };
  };

  // Handle cell click for toggling permission
  const handleCellClick = async (resource: string, action: string) => {
    if (!editable || !associationId || !userId) return;

    const cellKey = `${resource}-${action}`;
    setSavingCell(cellKey);

    try {
      const { hasPermission, customType } = getPermissionStatus(
        resource,
        action
      );
      const custom = findCustomPermission(resource, action);

      if (customType) {
        // Remove existing custom permission
        if (custom) {
          await removeCustomPermission(associationId, userId, custom.id);
        }
      } else if (hasPermission) {
        // Has permission from role, add DENY to remove it
        await addCustomPermission(
          associationId,
          userId,
          resource,
          [action],
          "deny"
        );
      } else {
        // Doesn't have permission, add GRANT to add it
        await addCustomPermission(
          associationId,
          userId,
          resource,
          [action],
          "grant"
        );
      }

      // Refetch custom permissions and notify parent
      refetchCustom();
      onPermissionChange?.();
    } catch (err) {
      console.error("Error toggling permission:", err);
    } finally {
      setSavingCell(null);
    }
  };

  // Render permission cell
  const renderCell = (resource: string, action: string) => {
    const cellKey = `${resource}-${action}`;
    const isSaving = savingCell === cellKey;
    const { hasPermission, customType } = getPermissionStatus(resource, action);

    // Determine visual state
    const isGranted =
      customType === "grant" || (hasPermission && customType !== "deny");
    const isCustomGrant = customType === "grant";
    const isCustomDeny = customType === "deny";

    if (isSaving) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      );
    }

    const cellContent = isGranted ? (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          bgcolor: isCustomGrant ? "info.50" : "success.50",
          border: 2,
          borderColor: isCustomGrant ? "info.main" : "success.200",
          cursor: editable ? "pointer" : "default",
          "&:hover": editable
            ? {
                bgcolor: isCustomGrant ? "info.100" : "success.100",
                transform: "scale(1.05)",
              }
            : {},
          transition: "all 0.2s",
        }}
      >
        {isCustomGrant ? (
          <AddIcon sx={{ fontSize: 16, color: "info.main" }} />
        ) : (
          <CheckIcon sx={{ fontSize: 16, color: "success.main" }} />
        )}
        <Typography
          variant="caption"
          fontWeight="medium"
          color={isCustomGrant ? "info.dark" : "success.dark"}
        >
          {isCustomGrant ? "GRANT" : "Kyllä"}
        </Typography>
      </Box>
    ) : (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          bgcolor: isCustomDeny ? "error.50" : "action.hover",
          border: isCustomDeny ? 2 : 0,
          borderColor: isCustomDeny ? "error.main" : "transparent",
          cursor: editable ? "pointer" : "default",
          "&:hover": editable
            ? {
                bgcolor: isCustomDeny ? "error.100" : "action.selected",
                transform: "scale(1.05)",
              }
            : {},
          transition: "all 0.2s",
        }}
      >
        {isCustomDeny ? (
          <RemoveIcon sx={{ fontSize: 16, color: "error.main" }} />
        ) : (
          <CloseIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        )}
        <Typography
          variant="caption"
          color={isCustomDeny ? "error.dark" : "text.disabled"}
          fontWeight={isCustomDeny ? "medium" : "normal"}
        >
          {isCustomDeny ? "DENY" : "Ei"}
        </Typography>
      </Box>
    );

    if (editable) {
      const tooltipText = customType
        ? `Klikkaa poistaaksesi ${customType.toUpperCase()} -ohitus`
        : isGranted
        ? "Klikkaa estääksesi (DENY)"
        : "Klikkaa salliaksesi (GRANT)";

      return (
        <Tooltip title={tooltipText} arrow>
          <Box
            onClick={() => handleCellClick(resource, action)}
            sx={{ display: "inline-block" }}
          >
            {cellContent}
          </Box>
        </Tooltip>
      );
    }

    return cellContent;
  };

  return (
    <Box>
      {editable && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Klikkaa solua vaihtaaksesi käyttöoikeutta. GRANT lisää oikeuden, DENY
          poistaa sen.
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Resurssi</TableCell>
              {allActions.map((action) => (
                <TableCell
                  key={action}
                  align="center"
                  sx={{ fontWeight: "bold" }}
                >
                  {actionLabels[action] || action}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map((resource) => (
              <TableRow key={resource} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {resourceLabels[resource] || resource}
                  </Typography>
                </TableCell>
                {allActions.map((action) => (
                  <TableCell key={action} align="center">
                    {renderCell(resource, action)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
